/*
 * Purpose: Global encrypted secrets vault — reused across all AutoBlocks workflows
 * Last modified: 2026-06-25 | Agent
 * Completeness: 92
 */
import { create } from 'zustand';
import {
  decryptVault,
  encryptVault,
  hasLegacySecrets,
  importVaultFile,
  migrateLegacySecrets,
  payloadFromSecrets,
  readVaultBlob,
  secretsFromPayload,
  writeVaultBlob,
} from '@/lib/secretsVault';

export interface Secret {
  id: string;
  name: string;
  value: string;
  createdAt: Date;
  updatedAt?: Date;
  /** Global vault entries are available to every workflow and compose plan */
  scope: 'global';
}

interface SecretStore {
  secrets: Secret[];
  isUnlocked: boolean;
  /** True when AES-GCM vault blob exists in localStorage */
  hasEncryptedVault: boolean;
  /** True when old base64 `secrets` key still present (migrate on create) */
  legacySecretsPending: boolean;
  isSaving: boolean;

  createVault: (passphrase: string, confirm: string) => Promise<void>;
  unlockVault: (passphrase: string) => Promise<void>;
  lockVault: () => void;
  changePassphrase: (current: string, next: string, confirm: string) => Promise<void>;

  setSecret: (name: string, value: string) => Promise<void>;
  addSecret: (name: string, value: string) => Promise<void>;
  deleteSecret: (id: string) => Promise<void>;
  getSecret: (name: string) => string | null;
  hasSecret: (name: string) => boolean;

  exportVault: (passphrase: string) => Promise<string>;
  importVault: (json: string, passphrase: string, mode: 'merge' | 'replace') => Promise<number>;

  loadSecrets: () => void;
  refreshVaultStatus: () => void;
}

const genId = () => `sec-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

async function persistEncrypted(secrets: Secret[], passphrase: string): Promise<void> {
  const blob = await encryptVault(payloadFromSecrets(secrets), passphrase);
  writeVaultBlob(blob);
}

let sessionPassphrase: string | null = null;

function vaultStatus() {
  return {
    hasEncryptedVault: Boolean(readVaultBlob()),
    legacySecretsPending: hasLegacySecrets(),
  };
}

export const useSecretStore = create<SecretStore>((set, get) => ({
  secrets: [],
  isUnlocked: false,
  ...vaultStatus(),
  isSaving: false,

  createVault: async (passphrase, confirm) => {
    if (passphrase.length < 8) throw new Error('Vault passphrase must be at least 8 characters.');
    if (passphrase !== confirm) throw new Error('Passphrases do not match.');

    let secrets: Secret[] = [];
    if (hasLegacySecrets()) {
      secrets = migrateLegacySecrets();
    }

    set({ isSaving: true });
    try {
      await persistEncrypted(secrets, passphrase);
      sessionPassphrase = passphrase;
      set({ secrets, isUnlocked: true, ...vaultStatus(), isSaving: false });
    } catch (e) {
      set({ isSaving: false });
      throw e;
    }
  },

  unlockVault: async (passphrase) => {
    const blob = readVaultBlob();
    if (!blob) throw new Error('No vault found — create one first.');

    set({ isSaving: true });
    try {
      const payload = await decryptVault(blob, passphrase);
      const secrets = secretsFromPayload(payload);
      sessionPassphrase = passphrase;
      set({ secrets, isUnlocked: true, ...vaultStatus(), isSaving: false });
    } catch {
      set({ isSaving: false });
      throw new Error('Wrong passphrase or corrupted vault.');
    }
  },

  lockVault: () => {
    sessionPassphrase = null;
    set({ secrets: [], isUnlocked: false });
  },

  changePassphrase: async (current, next, confirm) => {
    if (next.length < 8) throw new Error('New passphrase must be at least 8 characters.');
    if (next !== confirm) throw new Error('New passphrases do not match.');
    await get().unlockVault(current);
    const { secrets } = get();
    await persistEncrypted(secrets, next);
    sessionPassphrase = next;
  },

  setSecret: async (name, value) => {
    const trimmedName = name.trim();
    if (!trimmedName || !value) throw new Error('Secret name and value required.');
    if (!get().isUnlocked || !sessionPassphrase) {
      throw new Error('Unlock your secrets vault first (Builder → Secrets).');
    }

    const now = new Date();
    set((state) => {
      const existing = state.secrets.find((s) => s.name === trimmedName);
      if (existing) {
        return {
          secrets: state.secrets.map((s) =>
            s.name === trimmedName ? { ...s, value, updatedAt: now } : s
          ),
        };
      }
      return {
        secrets: [
          ...state.secrets,
          {
            id: genId(),
            name: trimmedName,
            value,
            createdAt: now,
            updatedAt: now,
            scope: 'global',
          },
        ],
      };
    });

    set({ isSaving: true });
    try {
      await persistEncrypted(get().secrets, sessionPassphrase);
    } finally {
      set({ isSaving: false });
    }
  },

  addSecret: async (name, value) => {
    await get().setSecret(name, value);
  },

  deleteSecret: async (id) => {
    if (!get().isUnlocked || !sessionPassphrase) {
      throw new Error('Unlock your secrets vault first.');
    }
    set((state) => ({ secrets: state.secrets.filter((s) => s.id !== id) }));
    set({ isSaving: true });
    try {
      await persistEncrypted(get().secrets, sessionPassphrase);
    } finally {
      set({ isSaving: false });
    }
  },

  getSecret: (name) => {
    if (!get().isUnlocked) return null;
    return get().secrets.find((s) => s.name === name)?.value ?? null;
  },

  hasSecret: (name) => {
    if (!get().isUnlocked) return false;
    const v = get().secrets.find((s) => s.name === name)?.value;
    return Boolean(v && v.length > 0);
  },

  exportVault: async (passphrase) => {
    const { secrets, isUnlocked } = get();
    if (!isUnlocked) throw new Error('Unlock vault before exporting.');
    const blob = await encryptVault(payloadFromSecrets(secrets), passphrase);
    return JSON.stringify(
      {
        ...blob,
        exportedAt: new Date().toISOString(),
        app: 'autoblocks',
        secretCount: secrets.length,
      },
      null,
      2
    );
  },

  importVault: async (json, passphrase, mode) => {
    if (!get().isUnlocked || !sessionPassphrase) {
      throw new Error('Unlock vault before importing.');
    }
    const imported = await importVaultFile(json, passphrase);
    const merged =
      mode === 'replace'
        ? imported
        : mergeSecrets(get().secrets, imported);

    set({ secrets: merged, isSaving: true });
    try {
      await persistEncrypted(merged, sessionPassphrase);
      return imported.length;
    } finally {
      set({ isSaving: false });
    }
  },

  loadSecrets: () => {
    set(vaultStatus());
  },

  refreshVaultStatus: () => {
    set(vaultStatus());
  },
}));

function mergeSecrets(existing: Secret[], imported: Secret[]): Secret[] {
  const map = new Map<string, Secret>();
  for (const s of existing) map.set(s.name, s);
  for (const s of imported) {
    const prev = map.get(s.name);
    map.set(s.name, {
      ...s,
      id: prev?.id ?? s.id,
      createdAt: prev?.createdAt ?? s.createdAt,
      updatedAt: new Date(),
      scope: 'global',
    });
  }
  return [...map.values()];
}

/** Test-only: unlock empty vault without UI */
export async function unlockVaultForTests(passphrase = 'test-vault-passphrase-123'): Promise<void> {
  const store = useSecretStore.getState();
  if (!store.hasEncryptedVault) {
    await store.createVault(passphrase, passphrase);
  } else {
    await store.unlockVault(passphrase);
  }
}
